import React, { useState, useEffect } from 'react';
import './Auth.css';
import { useNavigate } from 'react-router-dom';
import { getApiEndpoint } from './config';
import soundEffects from './sounds';

const Signup = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        first_name: '',
        last_name: '',
        beta_request_reason: ''
    });
    const [surveyConsent, setSurveyConsent] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [fieldsCompleted, setFieldsCompleted] = useState({
        first_name: false,
        last_name: false,
        email: false,
        password: false,
        confirmPassword: false,
        beta_request_reason: false,
        survey_consent: false
    });
    const [soundEnabled, setSoundEnabled] = useState(soundEffects.isEnabled());
    const [launchProgress, setLaunchProgress] = useState(0); // 0 to 1 for launch animation

    // Animate rocket to 100% when launching
    useEffect(() => {
        if (loading) {
            // Animate from 0 to 1 over 2 seconds
            const startTime = Date.now();
            const duration = 2000; // 2 seconds
            
            const animate = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                setLaunchProgress(progress);
                
                if (progress < 1) {
                    requestAnimationFrame(animate);
                }
            };
            
            requestAnimationFrame(animate);
        } else {
            setLaunchProgress(0);
        }
    }, [loading]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const updatedFormData = { ...formData, [name]: value };
        setFormData(updatedFormData);

        const newFieldsCompleted = { ...fieldsCompleted };
        const wasCompleted = newFieldsCompleted[name];

        if (name === 'confirmPassword') {
            newFieldsCompleted[name] = value.trim() && value === updatedFormData.password;
        } else if (name === 'password') {
            newFieldsCompleted[name] = value.trim() !== '';
            newFieldsCompleted['confirmPassword'] = updatedFormData.confirmPassword.trim() && updatedFormData.confirmPassword === value;
        } else if (name === 'beta_request_reason') {
            newFieldsCompleted[name] = value.trim().length >= 20;
        } else {
            newFieldsCompleted[name] = value.trim() !== '';
        }

        if (!wasCompleted && newFieldsCompleted[name]) soundEffects.beep();
        setFieldsCompleted(newFieldsCompleted);
    };

    const handleSurveyConsent = (e) => {
        const checked = e.target.checked;
        setSurveyConsent(checked);
        const newFieldsCompleted = { ...fieldsCompleted, survey_consent: checked };
        if (!fieldsCompleted.survey_consent && checked) soundEffects.beep();
        setFieldsCompleted(newFieldsCompleted);
    };

    const completedCount = Object.values(fieldsCompleted).filter(v => v).length;
    const fuelLevel = (completedCount / 7) * 100;
    const isReadyToLaunch = completedCount === 7;

    // Calculate rocket position along Bezier curve - using percentage-based coordinates
    // Quadratic Bezier formula: B(t) = (1-t)²P0 + 2(1-t)tP1 + t²P2
    // IMPORTANT: Rocket reaches 90% of journey when ALL FIELDS ARE COMPLETED
    // When launching (button clicked), it animates from 90% to 100% to reach the star
    let t;
    if (loading) {
        // During launch: animate from 0.9 to 1.0
        t = 0.9 + (launchProgress * 0.1);
    } else {
        // Normal state: capped at 0.9 (90% of journey)
        t = Math.min(fuelLevel / 100, 0.9);
    }
    // Positions as percentages of container
    const p0 = { x: 20, y: 85 };   // Start (Earth) - bottom left (20% from left, 85% from top)
    const p1 = { x: 40, y: 50 };   // Control point - middle
    const p2 = { x: 80, y: 15 };   // End (Star) - top right (80% from left, 15% from top)

    const rocketXPercent = Math.pow(1 - t, 2) * p0.x + 2 * (1 - t) * t * p1.x + Math.pow(t, 2) * p2.x;
    const rocketYPercent = Math.pow(1 - t, 2) * p0.y + 2 * (1 - t) * t * p1.y + Math.pow(t, 2) * p2.y;

    // Calculate angle based on the tangent of the Bezier curve for smooth rotation
    // Derivative of quadratic Bezier: B'(t) = 2(1-t)(P1-P0) + 2t(P2-P1)
    const tangentX = 2 * (1 - t) * (p1.x - p0.x) + 2 * t * (p2.x - p1.x);
    const tangentY = 2 * (1 - t) * (p1.y - p0.y) + 2 * t * (p2.y - p1.y);
    // Calculate angle from tangent (atan2 takes y, x but we reverse Y because it increases downward)
    const rocketAngle = Math.atan2(tangentX, -tangentY) * (180 / Math.PI);

    // Camera positioning - starts wide, zooms in progressively but caps at reasonable level
    const cameraScale = 1 + (t * 1.2); // Scale from 1x to 2.2x (reduced from 2.8x)
    const cameraOffsetX = t * (50 - rocketXPercent) * 6; // Pan to keep rocket centered (reduced intensity)
    const cameraOffsetY = t * (50 - rocketYPercent) * 6; // Pan to keep rocket centered vertically (reduced intensity)

    const containerTransform = `scale(${cameraScale}) translate(${cameraOffsetX}px, ${cameraOffsetY}px)`;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setHasError(false);

        // Validation
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            setHasError(true);
            soundEffects.errorBuzz();
            setTimeout(() => setHasError(false), 2000);
            return;
        }

        if (formData.password.length < 8) {
            setError('Password must be at least 8 characters');
            setHasError(true);
            soundEffects.errorBuzz();
            setTimeout(() => setHasError(false), 2000);
            return;
        }

        setLoading(true);
        soundEffects.blastOff();

        const signupData = {
            email: formData.email,
            password: formData.password,
            first_name: formData.first_name,
            last_name: formData.last_name,
            beta_request_reason: formData.beta_request_reason,
            survey_consent: surveyConsent,
        };

        try {
            const response = await fetch(getApiEndpoint('api/auth/signup'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(signupData)
            });

            const data = await response.json();

            if (data.success) {
                soundEffects.successChime();

                // Show success message about verification email
                setSuccess(data.message || 'Registration successful! Please check your email to verify your account.');

                // Wait for launch animation then redirect to login
                setTimeout(() => {
                    navigate('/login', {
                        state: {
                            message: 'Account created! Please verify your email first, then log in — your beta request will be reviewed shortly.',
                            email: formData.email
                        }
                    });
                }, 3000);
            } else {
                const fieldErrors = data.field_errors || {};
                const prioritizedFieldError = fieldErrors.email || fieldErrors.password || fieldErrors.first_name || fieldErrors.last_name;
                setError(prioritizedFieldError || data.error || 'Signup failed. Please review your details and try again.');
                setLoading(false);
                setHasError(true);
                soundEffects.errorBuzz();
                setTimeout(() => setHasError(false), 2000);
            }
        } catch (err) {
            console.error('Signup error:', err);
            const apiError = err?.response?.data?.error;
            const apiFieldErrors = err?.response?.data?.field_errors || {};
            const friendlyFieldError = apiFieldErrors.email || apiFieldErrors.password || apiFieldErrors.first_name || apiFieldErrors.last_name;
            if (err?.response?.status === 409) {
                setError(apiError || 'An account with this email already exists. Please sign in or reset your password.');
            } else {
                setError(friendlyFieldError || apiError || 'Signup failed due to a server or network issue. Please try again.');
            }
            setLoading(false);
            setHasError(true);
            soundEffects.errorBuzz();
            setTimeout(() => setHasError(false), 2000);
        }
    };

    const toggleSound = () => {
        const newState = soundEffects.toggle();
        setSoundEnabled(newState);
        if (newState) {
            soundEffects.beep();
        }
    };

    return (
        <div className="auth-container space-auth">
            <div className="stars-background"></div>

            {/* Sound toggle button */}
            <button className="sound-toggle" onClick={toggleSound} title={soundEnabled ? "Mute sounds" : "Enable sounds"}>
                {soundEnabled ? '🔊' : '🔇'}
            </button>

            <div className="signup-content">
                <div className="signup-left-pane">
                    <div className="signup-features-promo">
                        <p className="signup-features-eyebrow">New here?</p>
                        <h3>Not sure why to register yet?</h3>
                        <p>
                            Explore what Launchway can do before you create your account.
                        </p>
                        <span onClick={() => navigate('/features-of-launchway')} className="signup-features-link">
                            Explore Launchway features here
                        </span>
                    </div>

                    {/* Earth to Star Journey Visualization */}
                    <div className="rocket-journey-track">
                        <div className="journey-container" style={{ transform: containerTransform }}>
                        {/* Background stars field - optimized for performance */}
                        <div className="journey-starfield">
                            {[...Array(150)].map((_, i) => {
                                // Simple Linear Congruential Generator for consistent random positions
                                const lcg = (seed) => {
                                    const a = 1664525;
                                    const c = 1013904223;
                                    const m = Math.pow(2, 32);
                                    return ((a * seed + c) % m) / m;
                                };
                                
                                // Use different seeds for X and Y to ensure independence
                                const seedX = (i * 16807) % 2147483647;
                                const seedY = ((i + 73) * 48271) % 2147483647;
                                
                                const starLeft = lcg(seedX) * 100;
                                const starTop = lcg(seedY) * 100;
                                
                                // Calculate distance from rocket to this star (normalized)
                                const distanceToRocket = Math.sqrt(
                                    Math.pow(starLeft - rocketXPercent, 2) +
                                    Math.pow(starTop - rocketYPercent, 2)
                                );
                                const normalizedDistance = Math.max(0, Math.min(1, distanceToRocket / 100));

                                // Stars near the rocket brighten and enlarge
                                const proximityEffect = normalizedDistance < 0.3 ? (1 - normalizedDistance) * 0.5 : 0;

                                // Calculate dynamic brightness based on rocket progress and proximity
                                const baseOpacity = i % 7 === 0 ? 0.95 : i % 7 === 1 ? 0.85 : i % 7 === 2 ? 0.7 : i % 7 === 3 ? 0.55 : i % 7 === 4 ? 0.4 : i % 7 === 5 ? 0.25 : 0.15;
                                const progressFactor = (fuelLevel / 100) * 0.3; // Stars get 30% brighter as rocket progresses
                                const dynamicOpacity = Math.min(baseOpacity + progressFactor + proximityEffect, 1);

                                // Base star size
                                const baseSize = i % 5 === 0 ? 2.5 : i % 7 === 0 ? 2 : i % 9 === 0 ? 1.5 : 1;
                                // Stars near rocket grow slightly
                                const dynamicSize = baseSize * (1 + proximityEffect * 0.8);

                                // Parallax blur effect - stars blur based on speed (progress rate) and distance
                                // Stars further from rocket path blur more to create motion sensation
                                const speedFactor = fuelLevel / 100; // How fast we're moving
                                const blurIntensity = speedFactor > 0.2 ? speedFactor * normalizedDistance * 2 : 0;
                                const starFilter = blurIntensity > 0.1 ? `blur(${blurIntensity}px)` : 'none';

                                return (
                                    <div
                                        key={i}
                                        className="bg-star"
                                        style={{
                                            left: `${starLeft}%`,
                                            top: `${starTop}%`,
                                            animationDelay: `${(i % 6) * 0.7}s`,
                                            width: `${dynamicSize}px`,
                                            height: `${dynamicSize}px`,
                                            opacity: dynamicOpacity,
                                            filter: starFilter,
                                            transition: 'width 0.3s ease, height 0.3s ease, opacity 0.3s ease, filter 0.4s ease'
                                        }}
                                    />
                                );
                            })}
                        </div>

                        {/* Star at the top - DESTINATION */}
                        <div
                            className={`star-destination ${loading && launchProgress > 0.5 ? 'star-reached' : ''}`}
                            style={{
                                transform: `translate(-50%, -50%) scale(${loading && launchProgress > 0.5 ? 1 + (launchProgress - 0.5) * 2 : 1})`,
                                transition: 'transform 1s ease-out'
                            }}
                        >
                            <div className="star-icon">
                                <svg viewBox="0 0 120 120">
                                    <polygon
                                        points="60,20 71,48 101,48 77,67 87,97 60,78 33,97 43,67 19,48 49,48"
                                        fill="#FFD700"
                                        stroke="#FFA500"
                                        strokeWidth="2"
                                    />
                                    <circle cx="60" cy="60" r="15" fill="#FFF" opacity="0.9"/>
                                </svg>
                            </div>
                        </div>

                        {/* Journey Path - Curved diagonal trajectory from Earth to Star */}
                        <div className="journey-path">
                            <svg className="path-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                                {/* Curved path from bottom-left to top-right using percentages */}
                                <path
                                    d={`M ${p0.x},${p0.y} Q ${p1.x},${p1.y} ${p2.x},${p2.y}`}
                                    stroke="#6B7FD7"
                                    strokeWidth="0.3"
                                    strokeDasharray="2,1.5"
                                    fill="none"
                                    opacity="0.5"
                                />
                            </svg>

                            {/* Progress trail following curved path */}
                            <svg className="progress-trail-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                                {/* Dynamic trail that follows the curve from Earth to current rocket position */}
                                {fuelLevel > 0 && (
                                    <path
                                        d={`M ${p0.x},${p0.y} Q ${p0.x + t * (p1.x - p0.x)},${p0.y + t * (p1.y - p0.y)} ${rocketXPercent},${rocketYPercent}`}
                                        stroke="#FFD700"
                                        strokeWidth="0.5"
                                        fill="none"
                                        strokeLinecap="round"
                                        opacity="0.8"
                                    />
                                )}
                            </svg>
                        </div>

                        {/* Rocket traveling the curved journey */}
                        <div
                            className={`rocket-traveler ${loading ? 'launching' : ''} ${hasError ? 'error-state' : ''} ${isReadyToLaunch ? 'ready' : ''}`}
                            style={{
                                left: `${rocketXPercent}%`,
                                top: `${rocketYPercent}%`,
                                transform: `translate(-50%, -50%) rotate(${rocketAngle}deg) scale(${1 / cameraScale})`, // Inverse of camera scale to maintain consistent size
                                willChange: 'transform, left, top, opacity',
                                transition: loading ? 'all 2s ease-out' : 'transform 0.3s ease',
                                opacity: loading ? Math.max(0, 1 - (launchProgress * 1.5)) : 1, // Fade out during final approach
                                filter: `drop-shadow(0 0 ${15 + (fuelLevel / 100) * 25}px rgba(255, 215, 0, ${0.4 + (fuelLevel / 100) * 0.4}))`
                            }}
                        >
                            <svg viewBox="0 0 120 180" className="rocket-svg">
                                {/* Nose cone */}
                                <polygon points="60,15 40,50 80,50" fill="#E74C3C" stroke="#C0392B" strokeWidth="2"/>

                                {/* Body */}
                                <rect x="40" y="50" width="40" height="80" fill="#357ABD" stroke="#2E5C8A" strokeWidth="2" rx="5"/>

                                {/* Window */}
                                <circle cx="60" cy="80" r="12" fill="#4A90E2" stroke="#FFF" strokeWidth="2" opacity="0.9"/>
                                <circle cx="60" cy="80" r="8" fill="#FFF" opacity="0.4"/>

                                {/* Fins */}
                                <polygon points="40,120 25,145 40,135" fill="#F39C12" stroke="#D68910" strokeWidth="2"/>
                                <polygon points="80,120 95,145 80,135" fill="#F39C12" stroke="#D68910" strokeWidth="2"/>

                                {/* Boosters */}
                                <rect x="25" y="100" width="12" height="45" fill="#95A5A6" stroke="#7F8C8D" strokeWidth="2" rx="3"/>
                                <rect x="83" y="100" width="12" height="45" fill="#95A5A6" stroke="#7F8C8D" strokeWidth="2" rx="3"/>

                                {/* Engine nozzles */}
                                <rect x="44" y="128" width="32" height="8" fill="#34495E" stroke="#2C3E50" strokeWidth="1" rx="2"/>
                                <rect x="27" y="143" width="10" height="6" fill="#34495E" stroke="#2C3E50" strokeWidth="1" rx="1"/>
                                <rect x="83" y="143" width="10" height="6" fill="#34495E" stroke="#2C3E50" strokeWidth="1" rx="1"/>
                            </svg>

                            {/* Rocket flames when active */}
                            {(completedCount > 0 || loading) && !hasError && (
                                <div className="rocket-flames" style={{
                                    transform: `translateX(-50%) scaleY(${0.9 + (fuelLevel / 100) * 0.6})`
                                }}>
                                    <div className="flame main-engine"></div>
                                    <div className="flame side-engine left"></div>
                                    <div className="flame side-engine right"></div>
                                </div>
                            )}

                            {/* Error effects - smoke/explosion */}
                            {hasError && (
                                <div className="rocket-error-effects">
                                    <div className="error-smoke"></div>
                                    <div className="error-spark spark-1"></div>
                                    <div className="error-spark spark-2"></div>
                                    <div className="error-spark spark-3"></div>
                                </div>
                            )}

                            {/* Ready glow */}
                            {isReadyToLaunch && !loading && !hasError && (
                                <div className="rocket-ready-glow"></div>
                            )}
                        </div>

                        {/* Earth at the bottom - ORIGIN */}
                        <div
                            className="earth-origin"
                            style={{
                                transform: `translate(-50%, -50%)` // Just center it, no scaling or fading
                            }}
                        >
                            <div className="earth-icon">
                                <svg viewBox="0 0 140 140">
                                    {/* Outer atmospheric glow */}
                                    <circle cx="70" cy="70" r="55" fill="#4A9EFF" opacity="0.3"/>

                                    {/* Main Earth sphere - ocean */}
                                    <circle cx="70" cy="70" r="42" fill="#1E90FF"/>

                                    {/* Continents - more detailed and realistic shapes */}
                                    {/* North America region */}
                                    <path d="M 48,45 Q 52,42 56,45 L 58,50 Q 56,54 52,52 L 50,48 Z"
                                          fill="#2D5016" opacity="0.9"/>
                                    <path d="M 54,52 Q 58,50 62,53 L 65,58 Q 62,62 58,60 L 56,56 Z"
                                          fill="#3A6B1F" opacity="0.85"/>

                                    {/* South America region */}
                                    <path d="M 52,68 Q 54,64 58,66 L 60,72 Q 58,78 54,76 L 52,72 Z"
                                          fill="#2D5016" opacity="0.9"/>

                                    {/* Europe/Africa region */}
                                    <path d="M 68,48 Q 72,46 76,48 L 78,52 Q 76,56 72,54 Z"
                                          fill="#3A6B1F" opacity="0.85"/>
                                    <path d="M 70,58 Q 74,56 78,60 L 82,68 Q 80,74 76,72 L 72,66 Z"
                                          fill="#2D5016" opacity="0.9"/>

                                    {/* Asia region */}
                                    <path d="M 82,50 Q 88,48 92,52 L 94,58 Q 90,62 86,60 L 84,54 Z"
                                          fill="#3A6B1F" opacity="0.85"/>

                                    {/* Australia region */}
                                    <path d="M 88,74 Q 92,72 95,75 L 96,80 Q 94,84 90,82 Z"
                                          fill="#2D5016" opacity="0.85"/>

                                    {/* Atmosphere rim light */}
                                    <circle cx="70" cy="70" r="42" fill="none" stroke="#87CEEB" strokeWidth="1" opacity="0.6"/>
                                    <circle cx="70" cy="70" r="44" fill="none" stroke="#4A9EFF" strokeWidth="0.5" opacity="0.3"/>
                                </svg>
                            </div>
                        </div>

                        </div>
                    </div>
                </div>

                {/* Signup Form */}
                <div className="auth-form space-form signup-form">
                    <h2 className="space-title">
                        From <span className="title-accent">You</span> to <span className="title-accent">Star</span>
                    </h2>
                    <p className="journey-subtitle">Fuel Your Journey to Stardom</p>

                    {error && (
                        <div className="error-message asteroid-alert">
                            <svg className="alert-icon" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                            </svg>
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="success-message space-success-alert">
                            <svg className="alert-icon" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                            </svg>
                            {success}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="form-group space-form-group">
                            <label htmlFor="first_name">
                                First Name
                            </label>
                            <input
                                type="text"
                                id="first_name"
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleChange}
                                placeholder="Your first name"
                                className={fieldsCompleted.first_name ? 'field-completed' : ''}
                                required
                            />
                            {fieldsCompleted.first_name && <span className="field-check">✓</span>}
                        </div>

                        <div className="form-group space-form-group">
                            <label htmlFor="last_name">
                                Last Name
                            </label>
                            <input
                                type="text"
                                id="last_name"
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleChange}
                                placeholder="Your last name"
                                className={fieldsCompleted.last_name ? 'field-completed' : ''}
                                required
                            />
                            {fieldsCompleted.last_name && <span className="field-check">✓</span>}
                        </div>

                        <div className="form-group space-form-group">
                            <label htmlFor="email">
                                Email Address
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="your.star@galaxy.com"
                                className={fieldsCompleted.email ? 'field-completed' : ''}
                                required
                            />
                            {fieldsCompleted.email && <span className="field-check">✓</span>}
                        </div>

                        <div className="form-group space-form-group">
                            <label htmlFor="password">
                                Password
                            </label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="••••••••"
                                className={fieldsCompleted.password ? 'field-completed' : ''}
                                required
                            />
                            {fieldsCompleted.password && <span className="field-check">✓</span>}
                        </div>

                        <div className="form-group space-form-group">
                            <label htmlFor="confirmPassword">
                                Confirm Password
                            </label>
                            <input
                                type="password"
                                id="confirmPassword"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                placeholder="••••••••"
                                className={fieldsCompleted.confirmPassword ? 'field-completed' : ''}
                                required
                            />
                            {fieldsCompleted.confirmPassword && <span className="field-check">✓</span>}
                        </div>

                        <div className="form-group space-form-group">
                            <label htmlFor="beta_request_reason">
                                Why do you want beta access?
                            </label>
                            <textarea
                                id="beta_request_reason"
                                name="beta_request_reason"
                                value={formData.beta_request_reason}
                                onChange={handleChange}
                                placeholder="Tell us about your job search journey and how Launchway would help you…"
                                rows="4"
                                required
                                minLength="20"
                                className={fieldsCompleted.beta_request_reason ? 'field-completed' : ''}
                                style={{ resize: 'vertical' }}
                            />
                            <span className="field-check-inline">
                                {formData.beta_request_reason.length} / 20 min
                                {fieldsCompleted.beta_request_reason && ' ✓'}
                            </span>
                        </div>

                        <div className="form-group space-form-group checkbox-group">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={surveyConsent}
                                    onChange={handleSurveyConsent}
                                    required
                                />
                                <span>I will fill the weekly survey sent to my email.</span>
                                {fieldsCompleted.survey_consent && <span className="field-check">✓</span>}
                            </label>
                        </div>

                        <button
                            type="submit"
                            className={`auth-button rocket-launch-button ${
                                isReadyToLaunch ? 'ready-to-launch' : ''
                            } ${loading ? 'launching' : ''}`}
                            disabled={loading || !isReadyToLaunch}
                        >
                            {loading ? (
                                <>
                                    <span className="button-icon pulse"></span>
                                    LAUNCHING...
                                </>
                            ) : isReadyToLaunch ? (
                                <>
                                    <span className="button-icon pulse"></span>
                                    LAUNCH TO STARDOM
                                </>
                            ) : (
                                <>
                                    <span className="button-icon"></span>
                                    FUEL ROCKET ({Math.round(fuelLevel)}%)
                                </>
                            )}
                        </button>
                    </form>

                    <div className="auth-links space-links">
                        <p>Already a star? <span onClick={() => navigate('/login')} className="link">Sign In</span></p>
                        <p><span onClick={() => navigate('/')} className="link">← Back to Earth</span></p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Signup;
